import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import { useRefresh } from "../contexts/RefreshContext";
import { fmtCur, fmtCurCompact, fmtNum } from "../lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";
import { ArrowLeft, Package, ChartBar, CurrencyDollar, MagnifyingGlass, X, CircleNotch } from "@phosphor-icons/react";

const PALETTE = ["#1D4ED8", "#0F766E", "#9333EA", "#EA580C", "#0891B2", "#059669",
  "#DB2777", "#CA8A04", "#4F46E5", "#B91C1C", "#2A5C9B", "#7C3AED", "#0D9488"];

const compact = (n) => Number(n || 0).toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 });

const monthLabel = (m) => {
  if (!m || m === "unscheduled") return "Unscheduled";
  const [y, mo] = m.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[(parseInt(mo, 10) || 1) - 1]} ${y}`;
};

// Value of a monthly bucket for the active currency chip (all → ₹, else the native amount).
const valForCcy = (obj, ccy) => {
  if (!obj) return 0;
  if (ccy === "all") return obj.open_value_inr || 0;
  return (obj.by_ccy && (obj.by_ccy[ccy.toUpperCase()] || 0)) || 0;
};

function ChartPanel({ title, icon: Icon, data, dataKey, labelKey, ccy, isCurrency, onBarClick, testid, loading }) {
  const h = Math.max(220, data.length * 30 + 40);
  return (
    <div className="jde-panel" data-testid={testid}>
      <div className="jde-panel-header"><div className="jde-panel-title">{Icon && <Icon size={13} weight="bold" />} {title}</div></div>
      <div className="jde-panel-body" style={{ padding: "10px 8px 6px" }}>
        {loading && data.length === 0
          ? (
            <div data-testid={`${testid}-loading`} style={{ height: 240, display: "flex", flexDirection: "column",
                 alignItems: "center", justifyContent: "center", gap: 14, color: "var(--omh-primary)" }}>
              <CircleNotch size={40} weight="bold" className="omh-spin" />
              <div className="omh-pulse" style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.2, color: "var(--omh-text)" }}>
                Loading open orders…
              </div>
              <div style={{ fontSize: 12, color: "var(--omh-text-muted)" }}>Fetching live data from the ERP — this can take a few seconds.</div>
            </div>
          )
          : data.length === 0
          ? <div className="muted" style={{ padding: 20, textAlign: "center", fontSize: 12 }}>No data.</div>
          : (
            <ResponsiveContainer width="100%" height={h}>
              <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                <CartesianGrid horizontal={false} stroke="#EEF1F5" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }}
                  tickFormatter={(v) => (isCurrency ? fmtCurCompact(v, ccy) : compact(v))} />
                <YAxis type="category" dataKey={labelKey} width={150} tick={{ fontSize: 10.5, fill: "#334155" }}
                  interval={0} />
                <Tooltip cursor={{ fill: "rgba(29,78,216,0.06)" }}
                  formatter={(v) => [isCurrency ? fmtCur(v, ccy) : fmtNum(v), isCurrency ? "Value" : "Qty"]} />
                <Bar dataKey={dataKey} radius={[0, 4, 4, 0]} cursor={onBarClick ? "pointer" : "default"}
                  onClick={(d) => onBarClick && onBarClick(d)}>
                  {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
      </div>
    </div>
  );
}

export default function Explorer() {
  const [sp, setSp] = useSearchParams();
  const { tick } = useRefresh();
  const nav = useNavigate();
  const plant = sp.get("plant") || "";
  const ccy = (sp.get("ccy") || "all").toLowerCase();       // all | usd | inr
  const metric = (sp.get("metric") || "value").toLowerCase(); // value | qty
  const basis = (sp.get("basis") || "invoice").toLowerCase();  // invoice | po (price basis)
  const customer = sp.get("customer") || "";
  const view = (sp.get("view") || "customers").toLowerCase(); // customers | monthly
  const mcust = sp.get("mcust") || "";                        // monthly-tab selected customer
  const mmonth = sp.get("mmonth") || "";                      // monthly-tab selected month

  const [custData, setCustData] = useState(null);
  const [partData, setPartData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyParts, setMonthlyParts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partQuery, setPartQuery] = useState("");   // live item filter (by part # or description)

  const setParam = (patch) => {
    const p = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    setSp(p, { replace: true });
  };

  const modeQ = ccy === "usd" ? "usd" : ccy === "inr" ? "inr" : "";

  // customer-level breakdown
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (plant) q.set("plant", plant);
    if (modeQ) q.set("mode", modeQ);
    q.set("price_basis", basis);
    api.get(`/ms/explorer/customers?${q.toString()}`)
      .then((r) => setCustData(r.data)).catch(() => setCustData({ customers: [] }))
      .finally(() => setLoading(false));
  }, [plant, modeQ, basis, tick]);

  // part-level breakdown for the selected customer
  useEffect(() => {
    if (!customer) { setPartData(null); return; }
    setLoading(true);
    const q = new URLSearchParams();
    q.set("customer", customer);
    if (plant) q.set("plant", plant);
    if (modeQ) q.set("mode", modeQ);
    q.set("price_basis", basis);
    api.get(`/ms/explorer/parts?${q.toString()}`)
      .then((r) => setPartData(r.data)).catch(() => setPartData({ parts: [] }))
      .finally(() => setLoading(false));
  }, [customer, plant, modeQ, basis]);

  // monthly matrix (customer × month) for the Monthly tab
  useEffect(() => {
    if (view !== "monthly") return;
    setLoading(true);
    const q = new URLSearchParams();
    if (plant) q.set("plant", plant);
    if (modeQ) q.set("mode", modeQ);
    q.set("price_basis", basis);
    api.get(`/ms/explorer/monthly?${q.toString()}`)
      .then((r) => setMonthlyData(r.data)).catch(() => setMonthlyData({ months: [], customers: [] }))
      .finally(() => setLoading(false));
  }, [view, plant, modeQ, basis, tick]);

  // parts for a selected customer + month (Monthly-tab level 3)
  useEffect(() => {
    if (view !== "monthly" || !mcust || !mmonth) { setMonthlyParts(null); return; }
    setLoading(true);
    const q = new URLSearchParams();
    q.set("customer", mcust); q.set("month", mmonth);
    if (plant) q.set("plant", plant);
    if (modeQ) q.set("mode", modeQ);
    q.set("price_basis", basis);
    api.get(`/ms/explorer/monthly-parts?${q.toString()}`)
      .then((r) => setMonthlyParts(r.data)).catch(() => setMonthlyParts({ parts: [] }))
      .finally(() => setLoading(false));
  }, [view, mcust, mmonth, plant, modeQ, basis]);

  const displayCcy = ccy === "usd" ? "USD" : "INR";
  const isCurrency = metric === "value";

  // Each customer keeps its NATIVE currency for display (export → $, domestic → ₹). In the
  // "All" view the CHART ranks on the ₹-equivalent so mixed currencies compare fairly, while
  // the table shows the native amount (plus a ≈₹ note for non-INR customers).
  const custRows = useMemo(() => {
    const list = custData?.customers || [];
    return list.map((c) => {
      const nativeCcy = (c.primary_ccy || "INR").toUpperCase();
      const nativeVal = c.by_ccy?.[nativeCcy] || 0;
      const inrVal = c.open_value_inr || 0;
      const metricVal = metric === "qty" ? c.open_qty : (ccy === "all" ? inrVal : nativeVal);
      return { name: c.customer_name, nativeCcy, nativeVal, inrVal, byCcy: c.by_ccy || {},
        qty: c.open_qty, po_count: c.po_count, metricVal };
    }).sort((a, b) => b.metricVal - a.metricVal);
  }, [custData, ccy, metric]);

  const partRows = useMemo(() => {
    const list = partData?.parts || [];
    return list.map((p) => ({ name: p.part_no + (p.name ? ` · ${p.name.slice(0, 22)}` : ""),
      part_no: p.part_no, desc: p.name, qty: p.open_qty, value: p.open_value, currency: p.currency }));
  }, [partData]);

  // Reset the item filter whenever the selected customer changes.
  useEffect(() => { setPartQuery(""); }, [customer]);

  // Live-filtered parts for the table + totals (by part # OR description).
  const shownParts = useMemo(() => {
    const q = partQuery.trim().toLowerCase();
    if (!q) return partRows;
    return partRows.filter((p) =>
      String(p.part_no || "").toLowerCase().includes(q) ||
      String(p.desc || "").toLowerCase().includes(q));
  }, [partRows, partQuery]);
  const shownQty = useMemo(() => shownParts.reduce((a, p) => a + (Number(p.qty) || 0), 0), [shownParts]);
  const shownValue = useMemo(() => shownParts.reduce((a, p) => a + (Number(p.value) || 0), 0), [shownParts]);

  const partCcy = partRows[0]?.currency || displayCcy;
  const selectedPcode = useMemo(() => {
    const m = (custData?.customers || []).find((c) => c.customer_name === customer);
    return m?.ms_pcode || customer;
  }, [custData, customer]);
  const chartCust = custRows.slice(0, 20).slice().reverse().map((r) => ({ ...r }));
  const chartPartsQty = partRows.slice(0, 20).slice().reverse();
  const chartPartsVal = [...partRows].sort((a, b) => b.value - a.value).slice(0, 20).reverse();

  // ---- Monthly tab derived data ----
  const monthlyCustRows = useMemo(() => {
    const list = monthlyData?.customers || [];
    return list.map((c) => ({
      name: c.customer_name, ms_pcode: c.ms_pcode, qty: c.open_qty,
      value: valForCcy(c, ccy), months: c.months || {},
      nativeCcy: (c.primary_ccy || "INR").toUpperCase(),
    })).sort((a, b) => (metric === "qty" ? b.qty - a.qty : b.value - a.value));
  }, [monthlyData, ccy, metric]);
  const selMonthlyCust = useMemo(
    () => (monthlyData?.customers || []).find((c) => c.customer_name === mcust) || null,
    [monthlyData, mcust]);
  const monthRows = useMemo(() => {
    if (!selMonthlyCust) return [];
    const allMonths = monthlyData?.months || Object.keys(selMonthlyCust.months || {});
    return allMonths
      .filter((m) => selMonthlyCust.months?.[m])
      .map((m) => {
        const mv = selMonthlyCust.months[m];
        return { month: m, label: monthLabel(m), qty: mv.open_qty, value: valForCcy(mv, ccy),
          metricVal: metric === "qty" ? mv.open_qty : valForCcy(mv, ccy) };
      });
  }, [selMonthlyCust, monthlyData, ccy, metric]);
  const monthChart = monthRows.map((r) => ({ ...r, name: r.label })).slice().reverse();
  const monthlyPartRows = useMemo(() => {
    const list = monthlyParts?.parts || [];
    return list.map((p) => ({ part_no: p.part_no, desc: p.name, qty: p.open_qty,
      value: p.open_value, currency: p.currency }));
  }, [monthlyParts]);

  const Chip = ({ v, label, active, onClick, testid }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
        border: `1px solid ${active ? "var(--omh-primary)" : "var(--omh-border-strong)"}`,
        background: active ? "var(--omh-primary)" : "white", color: active ? "white" : "var(--omh-text)" }}>
      {label}
    </button>
  );

  return (
    <Layout breadcrumb={customer ? ["Home", "Explorer", customer] : ["Home", "Open Order Explorer"]}>
      <div className="jde-page">
        {/* CONTROLS */}
        <div className="jde-toolbar" data-testid="explorer-toolbar" style={{ flexWrap: "wrap", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="explorer-tabs">
            <Chip testid="explorer-tab-customers" label="By Customer" active={view === "customers"}
              onClick={() => setParam({ view: "", mcust: "", mmonth: "" })} />
            <Chip testid="explorer-tab-monthly" label="Monthly" active={view === "monthly"}
              onClick={() => setParam({ view: "monthly", customer: "" })} />
          </div>
          {customer && view !== "monthly" && (
            <button type="button" data-testid="explorer-back"
              onClick={() => setParam({ customer: "" })}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
                border: "1px solid var(--omh-border-strong)", background: "white", borderRadius: 6,
                fontWeight: 600, cursor: "pointer", fontSize: 12.5 }}>
              <ArrowLeft size={14} weight="bold" /> All customers
            </button>
          )}
          {customer && (
            <Link to={`/pos?customer=${encodeURIComponent(selectedPcode)}`} data-testid="explorer-view-pos"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
                border: "1px solid var(--omh-border-strong)", background: "white", borderRadius: 6,
                fontWeight: 600, fontSize: 12.5, color: "var(--omh-primary)", textDecoration: "none" }}>
              View in PO table →
            </Link>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>CURRENCY</span>
            <Chip testid="explorer-ccy-all" label="All → ₹" active={ccy === "all"} onClick={() => setParam({ ccy: "" })} />
            <Chip testid="explorer-ccy-usd" label="USD" active={ccy === "usd"} onClick={() => setParam({ ccy: "usd" })} />
            <Chip testid="explorer-ccy-inr" label="INR" active={ccy === "inr"} onClick={() => setParam({ ccy: "inr" })} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>METRIC</span>
            <Chip testid="explorer-metric-value" label="Value" active={metric === "value"} onClick={() => setParam({ metric: "value" })} />
            <Chip testid="explorer-metric-qty" label="Quantity" active={metric === "qty"} onClick={() => setParam({ metric: "qty" })} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }} title="Invoice = customer transfer price (dbo.Partyi) where set, else the PO price — same basis as the Open Order Report">
            <span className="muted" style={{ fontSize: 11, fontWeight: 700 }}>PRICE</span>
            <Chip testid="explorer-basis-invoice" label="Invoice price" active={basis === "invoice"} onClick={() => setParam({ basis: "" })} />
            <Chip testid="explorer-basis-po" label="PO price" active={basis === "po"} onClick={() => setParam({ basis: "po" })} />
          </div>
          {loading && (
            <span data-testid="explorer-loading-pill" className="omh-pulse" style={{ display: "inline-flex", alignItems: "center", gap: 7,
              fontSize: 12, fontWeight: 800, color: "var(--omh-primary)", background: "rgba(29,78,216,0.08)",
              border: "1px solid rgba(29,78,216,0.25)", padding: "4px 11px", borderRadius: 999 }}>
              <CircleNotch size={14} weight="bold" className="omh-spin" /> Loading open orders…
            </span>
          )}
        </div>

        {view === "monthly" ? (
          <>
            {(mcust || mmonth) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 12.5 }} data-testid="monthly-breadcrumb">
                <button type="button" data-testid="monthly-back-all" onClick={() => setParam({ mcust: "", mmonth: "" })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
                    border: "1px solid var(--omh-border-strong)", background: "white", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}>
                  <ArrowLeft size={14} weight="bold" /> All customers
                </button>
                {mcust && <span style={{ fontWeight: 700 }}>{mcust}</span>}
                {mmonth && <><span className="muted">›</span><span style={{ fontWeight: 700 }}>{monthLabel(mmonth)}</span></>}
                {mmonth && (
                  <button type="button" data-testid="monthly-back-months" onClick={() => setParam({ mmonth: "" })}
                    style={{ marginLeft: 4, padding: "4px 10px", border: "1px solid var(--omh-border-strong)", background: "white", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 11.5 }}>
                    ← back to months
                  </button>
                )}
              </div>
            )}

            {!mcust ? (
              /* LEVEL 1 — customers with open orders (monthly totals) */
              <div className="jde-panel" data-testid="monthly-customers-table">
                <div className="jde-panel-header"><div className="jde-panel-title">
                  Open orders by customer — pick a customer to see the monthly profile ({monthlyCustRows.length})
                </div></div>
                <div className="jde-panel-body" style={{ padding: 0 }}>
                  <table className="jde-grid">
                    <thead><tr>
                      <th>Customer</th>
                      <th style={{ textAlign: "right" }}>Open Qty</th>
                      <th style={{ textAlign: "right" }}>Open Value{ccy === "all" ? " (₹)" : ` (${displayCcy})`}</th>
                      <th style={{ textAlign: "right" }}>Months</th>
                      <th></th>
                    </tr></thead>
                    <tbody>
                      {monthlyCustRows.map((c) => (
                        <tr key={c.name} data-testid={`monthly-cust-row-${c.name}`}
                          onClick={() => setParam({ mcust: c.name })} style={{ cursor: "pointer" }}>
                          <td><strong>{c.name}</strong></td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(c.qty)}</td>
                          <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>
                            {fmtCurCompact(c.value, ccy === "all" ? "INR" : displayCcy)}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{Object.keys(c.months || {}).length}</td>
                          <td style={{ textAlign: "right", color: "var(--omh-primary)", fontSize: 11, fontWeight: 700 }}>drill in →</td>
                        </tr>
                      ))}
                      {monthlyCustRows.length === 0 && !loading && (
                        <tr><td colSpan={5} className="muted" style={{ padding: 18, textAlign: "center" }}>No open orders.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : !mmonth ? (
              /* LEVEL 2 — selected customer's open orders by month */
              <>
                <ChartPanel testid="monthly-chart"
                  title={`${mcust} — open ${isCurrency ? "value" : "qty"} by month${ccy === "all" ? " (₹)" : ` (${displayCcy})`}`}
                  icon={ChartBar} data={monthChart} dataKey="metricVal" labelKey="name" loading={loading}
                  ccy={ccy === "all" ? "INR" : displayCcy} isCurrency={isCurrency}
                  onBarClick={(d) => d?.month && setParam({ mmonth: d.month })} />
                <div className="jde-panel" data-testid="monthly-months-table" style={{ marginTop: 16 }}>
                  <div className="jde-panel-header"><div className="jde-panel-title">
                    {mcust} — monthly open ({monthRows.length} months)
                  </div></div>
                  <div className="jde-panel-body" style={{ padding: 0 }}>
                    <table className="jde-grid">
                      <thead><tr>
                        <th>Month (target dispatch)</th>
                        <th style={{ textAlign: "right" }}>Open Qty</th>
                        <th style={{ textAlign: "right" }}>Open Value{ccy === "all" ? " (₹)" : ` (${displayCcy})`}</th>
                        <th></th>
                      </tr></thead>
                      <tbody>
                        {monthRows.map((m) => (
                          <tr key={m.month} data-testid={`monthly-month-row-${m.month}`}
                            onClick={() => setParam({ mmonth: m.month })} style={{ cursor: "pointer" }}>
                            <td><strong>{m.label}</strong></td>
                            <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(m.qty)}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>{fmtCurCompact(m.value, ccy === "all" ? "INR" : displayCcy)}</td>
                            <td style={{ textAlign: "right", color: "var(--omh-primary)", fontSize: 11, fontWeight: 700 }}>parts →</td>
                          </tr>
                        ))}
                        {monthRows.length === 0 && !loading && (
                          <tr><td colSpan={4} className="muted" style={{ padding: 18, textAlign: "center" }}>No open orders for this customer.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              /* LEVEL 3 — parts open for customer + month */
              <div className="jde-panel" data-testid="monthly-parts-table">
                <div className="jde-panel-header"><div className="jde-panel-title">
                  {mcust} · {monthLabel(mmonth)} — open parts ({monthlyPartRows.length})
                </div></div>
                <div className="jde-panel-body" style={{ padding: 0 }}>
                  <table className="jde-grid">
                    <thead><tr>
                      <th>Part #</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Open Qty</th>
                      <th style={{ textAlign: "right" }}>Open Value</th>
                    </tr></thead>
                    <tbody>
                      {monthlyPartRows.map((p) => (
                        <tr key={p.part_no} data-testid={`monthly-part-row-${p.part_no}`}>
                          <td className="text-mono" style={{ fontWeight: 700 }}>{p.part_no}<WmPart icode={p.part_no} /></td>
                          <td>{p.desc || "—"}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(p.qty)}</td>
                          <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}
                              title={fmtCur(p.value, p.currency)}>{fmtCurCompact(p.value, p.currency)}</td>
                        </tr>
                      ))}
                      {monthlyPartRows.length === 0 && !loading && (
                        <tr><td colSpan={4} className="muted" style={{ padding: 18, textAlign: "center" }}>No open parts in this month.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : !customer ? (
          <>
            <ChartPanel testid="explorer-customers-chart"
              title={`Open ${isCurrency ? "value" : "quantity"} by customer${ccy === "all" ? " (all → ₹)" : ` (${displayCcy})`}`}
              icon={ChartBar} data={chartCust} dataKey="metricVal" labelKey="name" loading={loading}
              ccy={displayCcy} isCurrency={isCurrency}
              onBarClick={(d) => d?.name && setParam({ customer: d.name })} />

            <div className="jde-panel" data-testid="explorer-customers-table" style={{ marginTop: 16 }}>
              <div className="jde-panel-header"><div className="jde-panel-title">
                All customers with open orders ({custRows.length})
              </div></div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <table className="jde-grid">
                  <thead><tr>
                    <th>Customer</th>
                    <th style={{ textAlign: "right" }}>Open POs</th>
                    <th style={{ textAlign: "right" }}>Open Qty</th>
                    <th style={{ textAlign: "right" }}>Open Value (native)<div className="muted" style={{ fontSize: 9, fontWeight: 400 }} data-testid="explorer-basis-label">@ {basis === "po" ? "PO price" : "Invoice price"}</div></th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {custRows.map((c) => (
                      <tr key={c.name} data-testid={`explorer-cust-row-${c.name}`}
                        onClick={() => setParam({ customer: c.name })} style={{ cursor: "pointer" }}>
                        <td><strong>{c.name}</strong></td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(c.po_count)}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(c.qty)}</td>
                        <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>
                          {(() => {
                            const ccys = Object.entries(c.byCcy || {}).filter(([, v]) => v);
                            if (ccys.length === 0) return "—";
                            return ccys.map(([cc, v]) => (
                              <div key={cc} title={fmtCur(v, cc)}>{fmtCurCompact(v, cc)}</div>
                            ));
                          })()}
                        </td>
                        <td style={{ textAlign: "right", color: "var(--omh-primary)", fontSize: 11, fontWeight: 700 }}>drill in →</td>
                      </tr>
                    ))}
                    {custRows.length === 0 && !loading && (
                      <tr><td colSpan={5} className="muted" style={{ padding: 18, textAlign: "center" }}>No open orders.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginTop: 4 }}>
              <ChartPanel testid="explorer-parts-qty-chart" title="Open quantity by part" icon={Package}
                data={chartPartsQty} dataKey="qty" labelKey="name" isCurrency={false} loading={loading} />
              <ChartPanel testid="explorer-parts-value-chart" title={`Open value by part (${partCcy})`} icon={CurrencyDollar}
                data={chartPartsVal} dataKey="value" labelKey="name" ccy={partCcy} isCurrency loading={loading} />
            </div>

            <div className="jde-panel" data-testid="explorer-parts-table" style={{ marginTop: 16 }}>
              <div className="jde-panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div className="jde-panel-title">
                  {customer} — parts ({shownParts.length}{partQuery ? ` of ${partRows.length}` : ""})
                </div>
                <div style={{ position: "relative", width: "min(320px, 100%)" }}>
                  <MagnifyingGlass size={14} weight="bold"
                    style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                  <input
                    data-testid="explorer-part-search"
                    value={partQuery}
                    onChange={(e) => setPartQuery(e.target.value)}
                    placeholder="Filter by item # or description…"
                    style={{ width: "100%", padding: "7px 28px 7px 30px", fontSize: 12.5,
                      border: "1px solid var(--omh-border)", borderRadius: 8, outline: "none" }} />
                  {partQuery && (
                    <button data-testid="explorer-part-search-clear" onClick={() => setPartQuery("")}
                      aria-label="Clear search"
                      style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                        background: "transparent", border: "none", cursor: "pointer", color: "#64748b", display: "flex" }}>
                      <X size={13} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <table className="jde-grid">
                  <thead><tr>
                    <th>Part #</th>
                    <th>Description</th>
                    <th style={{ textAlign: "right" }}>Open Qty</th>
                    <th style={{ textAlign: "right" }}>Open Value</th>
                  </tr></thead>
                  <tbody>
                    {shownParts.map((p) => (
                      <tr key={p.part_no} data-testid={`explorer-part-row-${p.part_no}`}>
                        <td className="text-mono" style={{ fontWeight: 700 }}>{p.part_no}<WmPart icode={p.part_no} /></td>
                        <td>{p.desc || "—"}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(p.qty)}</td>
                        <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}
                            title={fmtCur(p.value, p.currency)}>{fmtCurCompact(p.value, p.currency)}</td>
                      </tr>
                    ))}
                    {shownParts.length === 0 && !loading && (
                      <tr><td colSpan={4} className="muted" style={{ padding: 18, textAlign: "center" }}>{partQuery ? "No items match your search." : "No open parts for this customer."}</td></tr>
                    )}
                  </tbody>
                  {shownParts.length > 0 && (
                    <tfoot><tr data-testid="explorer-parts-totals" style={{ background: "#f8fafc" }}>
                      <td style={{ fontWeight: 800, borderTop: "2px solid var(--omh-border)" }} colSpan={2}>Total · {shownParts.length} parts</td>
                      <td className="text-mono" style={{ textAlign: "right", fontWeight: 800, borderTop: "2px solid var(--omh-border)" }}>{fmtNum(shownQty)}</td>
                      <td className="text-mono" style={{ textAlign: "right", fontWeight: 800, borderTop: "2px solid var(--omh-border)" }}
                          title={fmtCur(shownValue, partCcy)}>{fmtCurCompact(shownValue, partCcy)}</td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
